import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FileDown, FileText } from "lucide-react";

export default function Reports() {
  const [equipment, setEquipment] = useState([]);
  const [form, setForm] = useState({
    start: new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10),
    end: new Date().toISOString().slice(0, 10),
    equipment_id: "all",
    category: "all",
  });

  useEffect(() => {
    api.get("/equipment").then(({ data }) => setEquipment(data.items)).catch(() => {});
  }, []);

  const exportBatch = () => {
    const params = new URLSearchParams();
    if (form.start) params.set("start", new Date(form.start).toISOString());
    if (form.end) params.set("end", new Date(form.end + "T23:59:59").toISOString());
    if (form.equipment_id !== "all") params.set("equipment_id", form.equipment_id);
    if (form.category !== "all") params.set("category", form.category);
    const url = `${API_BASE}/export/batch?${params.toString()}`;
    window.open(url, "_blank");
  };

  const quickRange = (days) => {
    const end = new Date();
    const start = new Date(Date.now() - days * 86400000);
    setForm({ ...form, start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) });
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-3xl mx-auto">
      <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">// Logbook Export</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-2">Reports</h1>
      <p className="text-zinc-400 mb-8">Generate PDF records for taxes, resale, or your own logbook.</p>

      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 sm:p-8 space-y-5">
        <div className="flex flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mr-2 self-center">Quick range:</div>
          <Button type="button" size="sm" variant="secondary" onClick={() => quickRange(7)} className="bg-zinc-800 hover:bg-zinc-700 rounded-sm text-xs uppercase">7 days</Button>
          <Button type="button" size="sm" variant="secondary" onClick={() => quickRange(30)} className="bg-zinc-800 hover:bg-zinc-700 rounded-sm text-xs uppercase">30 days</Button>
          <Button type="button" size="sm" variant="secondary" onClick={() => quickRange(90)} className="bg-zinc-800 hover:bg-zinc-700 rounded-sm text-xs uppercase">90 days</Button>
          <Button type="button" size="sm" variant="secondary" onClick={() => quickRange(365)} className="bg-zinc-800 hover:bg-zinc-700 rounded-sm text-xs uppercase">1 year</Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Start date</Label>
            <Input type="date" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono" data-testid="report-start" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">End date</Label>
            <Input type="date" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono" data-testid="report-end" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="report-category"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="Home">Home</SelectItem>
                <SelectItem value="Work">Work (tax)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Equipment</Label>
            <Select value={form.equipment_id} onValueChange={(v) => setForm({ ...form, equipment_id: v })}>
              <SelectTrigger className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="report-equipment"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-80">
                <SelectItem value="all">All equipment</SelectItem>
                {equipment.map((e) => <SelectItem key={e.equipment_id} value={e.equipment_id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button onClick={exportBatch} className="w-full bg-orange-600 hover:bg-orange-500 text-white uppercase tracking-widest font-bold rounded-sm py-6" data-testid="generate-report">
          <FileDown className="w-5 h-5 mr-2" /> Generate PDF report
        </Button>
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
          <FileText className="w-6 h-6 text-orange-500 mb-2" />
          <h3 className="font-display font-bold text-lg tracking-tight mb-1">Per-equipment PDFs</h3>
          <p className="text-sm text-zinc-400">From any equipment page, hit "Export PDF" for a full service history packet — perfect when selling.</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
          <FileDown className="w-6 h-6 text-orange-500 mb-2" />
          <h3 className="font-display font-bold text-lg tracking-tight mb-1">Single-record PDFs</h3>
          <p className="text-sm text-zinc-400">Each maintenance entry has a PDF button — great for warranty claims or receipts.</p>
        </div>
      </div>
    </div>
  );
}
